import { Model } from 'app/model/backendmodel';
import { OracleSink } from 'app/model/pipeline/copysink/oracle.copysink';
import { AdapterRegistry, IModel, Metamodel, ModelConstants, SharedModel } from 'shell-models-lib';

import { TabularSinkAdapter } from 'app/model/adapters/activities/copysink/tabular.sink.adapter';
import { Activity } from 'app/model/pipeline/activity';
import { Owned } from 'app/model/base/named';
import { CopySinkAdapter } from './copysink.adapter';
import { ValueAdapter } from '../../value.adapter';
import { GlobalVariables } from 'app/services/globals';
import { writeBehaviorOptionValues } from 'app/shared/constants/write-behavior.const';
import { UPDATE_METHOD_TO_WRITE_BEHAVIOR_MAP } from 'app/registries/consts/copy-job-const';

export const oracleSinkAdapts = {
    type: '*',
    writeBatchTimeout: '*',
    writeBatchSize: '*',
    preCopyScript: '*',
    maxConcurrentConnections: '*',
    structure: '*',
    writeBehavior: GlobalVariables.oracleUpsert ? '*' : undefined,
    upsertSettings: GlobalVariables.oracleUpsert ? '*' : undefined
};

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(OracleSink),
    asynchronous: true,
    adapts: oracleSinkAdapts
})
export class OracleSinkAdapter extends CopySinkAdapter<OracleSink, Model.OracleSink> {
    protected model(): IModel {
        return OracleSink.MODEL;
    }

    protected override async _toResource(model: OracleSink): Promise<Model.OracleSink> {
        const result: Model.OracleSink = {
            type: this.model().name
        };
        TabularSinkAdapter.toSinkResource(model, result);
        result.writeBatchSize = ValueAdapter.toResource(
            model,
            Metamodel.modelFor(OracleSink).attribute('writeBatchSize')
        );
        if (
            GlobalVariables.oracleUpsert ||
            (Metamodel.modelOf(model?.owner)?.name === ModelConstants.copyJobActivity)
        ) {
            let writeBehavior = ValueAdapter.toResource(model, OracleSink.WRITE_BEHAVIOR);
            // copy job has different write behavior values
            if (typeof writeBehavior === 'string' && UPDATE_METHOD_TO_WRITE_BEHAVIOR_MAP.has(writeBehavior)) {
                writeBehavior = UPDATE_METHOD_TO_WRITE_BEHAVIOR_MAP.get(writeBehavior);
            }
            result.writeBehavior = writeBehavior;
            if (
                writeBehavior === writeBehaviorOptionValues.upsert ||
                writeBehavior === writeBehaviorOptionValues.SCD2 ||
                ValueAdapter.isTemplated(model, OracleSink.WRITE_BEHAVIOR)
            ) {
                this._upsertToResource(result, model);
            }
        }
        CopySinkAdapter.toCommonCopySinkResource(model, result, this.context);
        return result;
    }

    protected override async _toModel(resource: Model.OracleSink, owner: Activity): Promise<OracleSink> {
        const result = Owned.create<OracleSink>(this.model(), owner);
        TabularSinkAdapter.toSinkModel(resource, result);
        ValueAdapter.toModel(
            result,
            Metamodel.modelFor(OracleSink).attribute('writeBatchSize'),
            resource.writeBatchSize
        );
        if (GlobalVariables.oracleUpsert || Metamodel.modelOf(owner)?.name === ModelConstants.copyJobActivity) {
            if (resource.writeBehavior === undefined) {
                delete result.writeBehavior;
            } else {
                ValueAdapter.toModel(result, OracleSink.WRITE_BEHAVIOR, resource.writeBehavior);
            }
            if (
                resource.writeBehavior === writeBehaviorOptionValues.upsert ||
                resource.writeBehavior === writeBehaviorOptionValues.SCD2 ||
                ValueAdapter.isTemplated(result, OracleSink.WRITE_BEHAVIOR)
            ) {
                this._upsertToModel(resource, result);
            }
        }
        CopySinkAdapter.toCommonCopySinkModel(result, resource);
        return result;
    }

    private _upsertToResource(result: Model.OracleSink, model: OracleSink): void {
        result.upsertSettings = {
            keys: ValueAdapter.toResource(model, OracleSink.WRITE_BEHAVIOR_KEYS)
        };
    }

    private _upsertToModel(resource: Model.OracleSink, result: OracleSink): void {
        // for copy-wizard generated activities, where upsertSettings parameterized as one
        if (
            resource.upsertSettings &&
            (resource.upsertSettings as SharedModel.ExpressionValue)?.type !== 'Expression'
        ) {
            const upsertSettings = resource.upsertSettings as Model.WriteBehaviorUpsertOptions;
            ValueAdapter.toModel(result, OracleSink.WRITE_BEHAVIOR_KEYS, upsertSettings.keys);
        }
    }
}
